import { SupabaseClient, REALTIME_SUBSCRIBE_STATES } from "@supabase/supabase-js";
import { DocumentRepository } from "../repositories/document.repository";
import { ProjectUserRepository } from "../repositories/project-user.repository";
import {
  DocumentDTO,
  CreateDocumentDTO,
  UpdateDocumentDTO,
  DocumentSummaryDTO,
  DocumentWithChunkDTO,
  DocumentChunkDTO,
} from "../dtos";
import { DocumentMapper } from "../mappers/document.mapper";
import {
  PaginationOptions,
  PaginatedResponse,
} from "../types/pagination.types";
import { UserRole } from "../types/user-role.enum";

export class DocumentService {
  private documentRepository: DocumentRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.documentRepository = new DocumentRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * Import web page as document
   */
  async importWeb(
    projectId: string,
    url: string,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Use fetch directly to get proper error messages from non-2xx responses
      const supabaseUrl = (this.supabase as any).supabaseUrl;
      const functionUrl = `${supabaseUrl}/functions/v1/import-web`;
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Accept-Language': typeof navigator !== 'undefined' ? navigator.language : 'en',
        },
        body: JSON.stringify({
          projectId,
          url,
        }),
      });

      const result = await response.json();
      
      // Check if we have an error response
      if (!response.ok || !result.success) {
        // Extract the error message from the response data
        throw new Error(result.error || "Failed to import web page");
      }

      return result.document as DocumentDTO;
    } catch (error) {
      // Don't wrap the error message if it's already an Error with a message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        `Failed to import web page: ${error || "Unknown error"}`,
      );
    }
  }

  /**
   * Import YouTube video as document
   */
  async importYoutube(
    projectId: string,
    url: string,
    userId: string,
    isOnboarding: boolean = false,
  ): Promise<DocumentDTO> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Use fetch directly to get proper error messages from non-2xx responses
      const supabaseUrl = (this.supabase as any).supabaseUrl;
      const functionUrl = `${supabaseUrl}/functions/v1/import-youtube`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Accept-Language': typeof navigator !== 'undefined' ? navigator.language : 'en',
        },
        body: JSON.stringify({
          projectId,
          url,
          isOnboarding,
        }),
      });

      const result = await response.json();
      
      // Check if we have an error response
      if (!response.ok || !result.success) {
        // Extract the error message from the response data
        throw new Error(result.error || "Failed to import YouTube video");
      }

      return result.document as DocumentDTO;
    } catch (error) {
      // Don't wrap the error message if it's already an Error with a message
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(
        `Failed to import YouTube video: ${error || "Unknown error"}`,
      );
    }
  }

  /**
   * Import PDF file as document
   */
  async importPdf(
    projectId: string,
    fileUrl: string,
    fileName: string,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Call Edge Function
      const { data: result, error } = await this.supabase.functions.invoke(
        "import-pdf",
        {
          body: {
            projectId,
            fileUrl,
            fileName,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to import PDF");
      }

      return result.document as DocumentDTO;
    } catch (error) {
      throw new Error(
        `Failed to import PDF: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Import image file as document
   */
  async importImage(
    projectId: string,
    fileUrl: string,
    fileName: string,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Call Edge Function
      const { data: result, error } = await this.supabase.functions.invoke(
        "import-image",
        {
          body: {
            projectId,
            fileUrl,
            fileName,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to import image");
      }

      return result.document as DocumentDTO;
    } catch (error) {
      throw new Error(
        `Failed to import image: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 생성 - Edge Function 사용 (사용량 제한 체크 포함)
   */
  async createDocument(
    data: CreateDocumentDTO,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Edge Function 호출
      const { data: result, error } = await this.supabase.functions.invoke(
        "import-text",
        {
          body: {
            projectId: data.projectId,
            title: data.title,
            content: data.content,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to create document");
      }

      return result.document as DocumentDTO;
    } catch (error) {
      throw new Error(
        `Failed to create document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 내용 업데이트
   */
  async updateDocument(
    documentId: string,
    data: UpdateDocumentDTO,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // 1. 문서 조회
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 2. 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser || projectUser.role === UserRole.VIEWER) {
        throw new Error("Insufficient permissions to update document");
      }

      // 3. Entity 메서드를 사용하여 업데이트
      if (data.title !== undefined && data.content !== undefined) {
        document.updateContent(data.title, data.content, userId);
      } else if (data.title !== undefined) {
        document.title = data.title;
        document.updated_by_id = userId;
        document.updated_at = new Date();
        document["markAsDirty"]();
      } else if (data.content !== undefined) {
        document.content = data.content;
        document.updated_by_id = userId;
        document.updated_at = new Date();
        document["markAsDirty"]();
      }

      // 4. 변경사항 저장
      const savedDocument = await this.documentRepository.save(document);

      return DocumentMapper.toDTO(savedDocument);
    } catch (error) {
      throw new Error(
        `Failed to update document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 프로젝트의 문서 목록 조회 (페이지네이션 없음)
   */
  async getProjectDocuments(
    projectId: string,
    userId: string,
  ): Promise<DocumentDTO[]> {
    try {
      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        projectId,
        userId,
      );
      if (!projectUser) {
        throw new Error("User is not a member of this project");
      }

      // 페이지네이션 없이 조회
      const documents = await this.documentRepository.findByProject(projectId);
      return documents.map((doc) => DocumentMapper.toDTO(doc));
    } catch (error) {
      throw new Error(
        `Failed to get project documents: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 프로젝트의 문서 목록 조회 (페이지네이션 포함)
   */
  async getProjectDocumentsPaginated(
    projectId: string,
    userId: string,
    statusFilter?: "pending" | "approved" | "all",
    options?: PaginationOptions,
  ): Promise<PaginatedResponse<DocumentDTO>> {
    try {
      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        projectId,
        userId,
      );
      if (!projectUser) {
        throw new Error("User is not a member of this project");
      }

      // 페이지네이션과 함께 조회
      const result = await this.documentRepository.findByProjectPaginated(
        projectId,
        options || { page: 1 },
        statusFilter || "all",
      );

      return {
        data: result.data.map((doc) => DocumentMapper.toDTO(doc)),
        pagination: result.pagination,
      };
    } catch (error) {
      throw new Error(
        `Failed to get project documents: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 조회 (청크 포함)
   */
  async getDocument(
    documentId: string,
    userId: string,
  ): Promise<DocumentWithChunkDTO> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("Insufficient permissions to view document");
      }

      // Fetch metadata based on document type
      let metadata: any = null;
      if (document.type && document.type !== "text") {
        metadata = await this.fetchDocumentMetadata(documentId, document.type);
      }

      // Fetch chunks
      const { data: chunksData, error: chunksError } = await this.supabase
        .from("document_chunks")
        .select("*")
        .eq("document_id", documentId)
        .order("chunk_index", { ascending: true });

      if (chunksError) {
        throw chunksError;
      }

      const chunks: DocumentChunkDTO[] = (chunksData || []).map((chunk) => ({
        id: chunk.id,
        documentId: chunk.document_id,
        chunkIndex: chunk.chunk_index,
        chunkId: chunk.chunk_id,
        content: chunk.content,
        startOffset: chunk.start_offset,
        endOffset: chunk.end_offset,
        pageNumber: chunk.page_number,
        metadata: chunk.metadata,
        createdAt: new Date(chunk.created_at),
        updatedAt: new Date(chunk.updated_at),
      }));

      const dto = DocumentMapper.toDTO(document);
      if (metadata) {
        dto.metadata = metadata;
      }

      return {
        ...dto,
        chunks,
      };
    } catch (error) {
      throw new Error(
        `Failed to get document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 청크 조회
   */
  async getDocumentChunks(documentId: string, userId: string): Promise<any[]> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("Insufficient permissions to view document chunks");
      }

      const { data: chunks, error } = await this.supabase
        .from("document_chunks")
        .select("*")
        .eq("document_id", documentId)
        .order("chunk_index", { ascending: true });

      if (error) {
        throw error;
      }

      return chunks || [];
    } catch (error) {
      throw new Error(
        `Failed to get document chunks: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 인덱싱 트리거 (Edge Function 호출)
   */
  async triggerDocumentIndexing(
    documentId: string,
    userId: string,
    forceReindex: boolean = false,
  ): Promise<{ success: boolean; chunks_count: number }> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser || projectUser.role === UserRole.VIEWER) {
        throw new Error("Insufficient permissions to index document");
      }

      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Edge Function 호출
      const { data: result, error } = await this.supabase.functions.invoke(
        "index-and-sync-document",
        {
          body: {
            documentId,
            projectId: document.project_id,
            forceReindex,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to index document");
      }

      return {
        success: true,
        chunks_count: result.chunks_count || 0,
      };
    } catch (error) {
      throw new Error(
        `Failed to trigger document indexing: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 인덱싱 상태 조회
   */
  async getDocumentIndexingStatus(
    documentId: string,
    userId: string,
  ): Promise<{ status: string; indexed_at?: string; error?: string }> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("Insufficient permissions to view document status");
      }

      const { data, error } = await this.supabase
        .from("documents")
        .select("indexing_status, indexed_at, indexing_error")
        .eq("id", documentId)
        .single();

      if (error) {
        throw error;
      }

      return {
        status: data.indexing_status || "pending",
        indexed_at: data.indexed_at,
        error: data.indexing_error,
      };
    } catch (error) {
      throw new Error(
        `Failed to get indexing status: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Fetch document metadata based on type
   */
  private async fetchDocumentMetadata(
    documentId: string,
    type: string,
  ): Promise<any> {
    try {
      let tableName: string;
      switch (type) {
        case "youtube":
          tableName = "document_youtube";
          break;
        case "pdf":
          tableName = "document_pdf";
          break;
        case "image":
          tableName = "document_image";
          break;
        case "notion":
          tableName = "document_notion";
          break;
        case "slack":
          tableName = "document_slack";
          break;
        case "web":
          tableName = "document_web";
          break;
        default:
          return null;
      }

      const { data, error } = await this.supabase
        .from(tableName)
        .select("*")
        .eq("document_id", documentId)
        .maybeSingle();

      if (error) {
        console.error(`Error fetching metadata from ${tableName}:`, error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error fetching document metadata:", error);
      return null;
    }
  }

  /**
   * 문서 삭제 (draft 상태에서만 가능)
   */
  async deleteDocument(documentId: string, userId: string): Promise<void> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Call Edge Function to handle deletion (includes RAG deletion)
      const { data: result, error } = await this.supabase.functions.invoke(
        "delete-document",
        {
          body: {
            documentId,
            projectId: document.project_id,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to delete document");
      }
    } catch (error) {
      throw new Error(
        `Failed to delete document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Modify document content and title
   */
  async modifyDocument(
    documentId: string,
    { title, content }: { title: string; content: string },
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // Call Edge Function to handle modification (includes RAG re-indexing)
      const { data: result, error } = await this.supabase.functions.invoke(
        "modify-document",
        {
          body: {
            documentId,
            title,
            content,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        },
      );

      if (error) {
        throw error;
      }

      if (!result?.success) {
        throw new Error(result?.error || "Failed to modify document");
      }

      // Fetch updated document
      const updatedDocument = await this.documentRepository.findById(documentId);
      if (!updatedDocument) {
        throw new Error("Failed to fetch updated document");
      }

      return DocumentMapper.toDTO(updatedDocument);
    } catch (error) {
      throw new Error(
        `Failed to modify document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 승인 (pending → approved) - Edge Function 호출
   */
  async approveDocument(
    documentId: string,
    userId: string,
  ): Promise<DocumentDTO> {
    try {
      // 1. 문서 조회
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      // 2. 권한 확인 (프로젝트 멤버만 승인 가능)
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser || projectUser.role === UserRole.VIEWER) {
        throw new Error("Insufficient permissions to approve documents");
      }

      // 3. 상태 확인 (pending 상태에서만 승인 가능)
      if (document.status !== "pending") {
        throw new Error(
          `Cannot approve document in ${document.status} status. Only pending documents can be approved.`,
        );
      }

      // 4. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // 5. Edge Function 호출하여 승인 처리 (RAG 인덱싱 포함)
      const supabaseUrl = (this.supabase as any).supabaseUrl;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/document-approval`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            documentId,
            action: "approve",
          }),
        },
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to approve document: ${error}`);
      }

      const result = await response.json();

      // 승인된 문서 다시 조회
      const approvedDocument =
        await this.documentRepository.findById(documentId);
      if (!approvedDocument) {
        throw new Error("Failed to retrieve approved document");
      }

      return DocumentMapper.toDTO(approvedDocument);
    } catch (error) {
      throw new Error(
        `Failed to approve document: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * pending 문서 목록 조회
   */
  async getPendingDocuments(
    projectId: string,
    userId: string,
  ): Promise<DocumentDTO[]> {
    try {
      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        projectId,
        userId,
      );
      if (!projectUser) {
        throw new Error("User is not a member of this project");
      }

      const documents =
        await this.documentRepository.findPendingByProject(projectId);
      return documents.map((doc) => DocumentMapper.toDTO(doc));
    } catch (error) {
      throw new Error(
        `Failed to get pending documents: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * pending 문서 개수 조회
   */
  async getPendingCount(projectId: string, userId: string): Promise<number> {
    try {
      // 권한 확인
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        projectId,
        userId,
      );
      if (!projectUser) {
        throw new Error("User is not a member of this project");
      }

      return await this.documentRepository.getPendingCount(projectId);
    } catch (error) {
      throw new Error(
        `Failed to get pending count: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 요약 조회 (Edge Function 호출)
   */
  async getDocumentSummary(
    documentId: string,
    userId: string,
  ): Promise<DocumentSummaryDTO | null> {
    try {
      // 1. 문서 존재 및 권한 확인
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("Insufficient permissions to view document summary");
      }

      // 2. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // 3. Edge Function 호출 (매번 새로 생성)
      const supabaseUrl = (this.supabase as any).supabaseUrl;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/summarize-document`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            'Accept-Language': typeof navigator !== 'undefined' ? navigator.language : 'en',
          },
          body: JSON.stringify({ documentId }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Summarize document error:", errorText);
        throw new Error(
          `Failed to generate summary: ${response.statusText}`,
        );
      }

      const result = await response.json();

      if (!result.success || !result.summary) {
        return null; // 요약 생성 실패 시 null 반환
      }

      // 임시 ID와 타임스탬프로 DTO 반환
      return {
        id: `temp-${documentId}`,
        documentId,
        keyPoints: result.summary.keyPoints || [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
    } catch (error) {
      throw new Error(
        `Failed to get document summary: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * chunk_id로 문서 청크 조회
   */
  async getDocumentChunkByChunkId(
    chunkId: string,
    userId: string,
  ): Promise<DocumentChunkDTO | null> {
    try {
      // chunk_id로 청크 조회 - RLS 정책에 의해 권한이 확인됨
      const { data: chunkData, error: chunkError } = await this.supabase
        .from("document_chunks")
        .select(
          `
          *,
          documents!inner(
            id,
            project_id,
            title
          )
        `,
        )
        .eq("chunk_id", chunkId)
        .single();

      if (chunkError) {
        if (chunkError.code === "PGRST116") {
          return null; // 청크를 찾을 수 없음
        }
        throw chunkError;
      }

      if (!chunkData) {
        return null;
      }

      // 추가 권한 확인 (혹시나)
      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        chunkData.documents.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error("Insufficient permissions to view document chunk");
      }

      return {
        id: chunkData.id,
        documentId: chunkData.document_id,
        chunkIndex: chunkData.chunk_index,
        chunkId: chunkData.chunk_id,
        content: chunkData.content,
        startOffset: chunkData.start_offset,
        endOffset: chunkData.end_offset,
        pageNumber: chunkData.page_number,
        metadata: chunkData.metadata,
        createdAt: new Date(chunkData.created_at),
        updatedAt: new Date(chunkData.updated_at),
      };
    } catch (error) {
      throw new Error(
        `Failed to get document chunk: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * 문서 요약 생성 (Edge Function 호출)
   */
  async generateDocumentSummary(
    documentId: string,
    userId: string,
  ): Promise<DocumentSummaryDTO> {
    try {
      // 1. 문서 존재 및 권한 확인
      const document = await this.documentRepository.findById(documentId);
      if (!document) {
        throw new Error("Document not found");
      }

      const projectUser = await this.projectUserRepository.findByProjectAndUser(
        document.project_id,
        userId,
      );
      if (!projectUser) {
        throw new Error(
          "Insufficient permissions to generate document summary",
        );
      }

      // 2. Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required - no valid session found");
      }

      // 3. Edge Function 호출
      const supabaseUrl = (this.supabase as any).supabaseUrl;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/summarize-document`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            documentId,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to generate summary: ${error}`);
      }

      const result = await response.json();

      if (!result.success || !result.summary) {
        throw new Error(result.error || "Failed to generate summary");
      }

      // 3. 요약 데이터 조회 (Edge Function이 저장한 것)
      const { data: savedSummary } = await this.supabase
        .from("documents_summary")
        .select("*")
        .eq("document_id", documentId)
        .maybeSingle();

      if (!savedSummary) {
        // Edge Function이 저장하지 못한 경우, 반환된 데이터로 DTO 생성
        return {
          id: crypto.randomUUID(),
          documentId: documentId,
          keyPoints: result.summary.keyPoints || [],
          createdAt: new Date(),
          updatedAt: new Date(),
        };
      }

      return {
        id: savedSummary.id,
        documentId: savedSummary.document_id,
        keyPoints: savedSummary.key_points || [],
        createdAt: new Date(savedSummary.created_at),
        updatedAt: new Date(savedSummary.updated_at),
      };
    } catch (error) {
      throw new Error(
        `Failed to generate document summary: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }

  /**
   * Subscribe to real-time document updates for a project
   */
  async subscribeToProjectDocuments(
    projectId: string,
    userId: string,
    callbacks: {
      onConnect?: (status: REALTIME_SUBSCRIBE_STATES, err?: Error) => void;
      onInsert?: (document: DocumentDTO) => void;
      onUpdate?: (document: DocumentDTO) => void;
      onDelete?: (id: string) => void;
    }
  ): Promise<() => void> {
    return new Promise(async (resolve, reject) => {
      try {
        // Verify user has access to the project
        const projectUser = await this.projectUserRepository.findByProjectAndUser(
          projectId,
          userId
        );
        
        if (!projectUser) {
          throw new Error("User is not a member of this project");
        }

        // Subscribe to real-time updates
        const unsubscribe = this.documentRepository.subscribeToProjectDocuments(
          projectId,
          {
            onConnect: callbacks.onConnect,
            onInsert: callbacks.onInsert
              ? (document) => callbacks.onInsert!(DocumentMapper.toDTO(document))
              : undefined,
            onUpdate: callbacks.onUpdate
              ? (document) => callbacks.onUpdate!(DocumentMapper.toDTO(document))
              : undefined,
            onDelete: callbacks.onDelete,
          }
        );

        resolve(unsubscribe);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Unsubscribe from real-time updates for a project
   */
  unsubscribeFromProjectDocuments(projectId: string): void {
    this.documentRepository.unsubscribeFromProject(projectId);
  }

  /**
   * Unsubscribe from all real-time updates
   */
  unsubscribeFromAllDocuments(): void {
    this.documentRepository.unsubscribeAll();
  }

  /**
   * Get document change history
   */
  async getDocumentHistory(documentId: string): Promise<any[]> {
    try {
      const { data, error } = await this.supabase
        .from("document_history")
        .select("*")
        .eq("document_id", documentId)
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return (data || []).map((history) => ({
        id: history.id,
        documentId: history.document_id,
        editedBy: history.edited_by,
        beforeTitle: history.before_title,
        beforeContent: history.before_content,
        afterTitle: history.after_title,
        afterContent: history.after_content,
        createdAt: history.created_at,
      }));
    } catch (error) {
      throw new Error(
        `Failed to get document history: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  }
}
